import { uniqBy } from 'lodash';
import styled from 'styled-components';

import MoreContentRequester from 'components/MoreContentRequester';
import Button from 'components/shared/Button';
import StandardModal from 'components/shared/StandardModal';
import { simpleDateAtTime } from 'lib/date';
import {
  DischargeChecklistEntry,
  DischargeChecklistHistoryEntry,
  DischargeChecklistHistoryOperation,
} from 'models/DischargeChecklistItem';
import { useDischargeChecklistItemHistory } from 'services/api/dischargeChecklists';
import { colors } from 'styles/theme/colors';
import { devices } from 'styles/theme/devices';
import { BodySmall, Label } from 'styles/typography';

const STATE_WORDING: Partial<Record<DischargeChecklistHistoryOperation, string>> = {
  [DischargeChecklistHistoryOperation.CHECKED]: 'Item marked as complete',
  [DischargeChecklistHistoryOperation.UNCHECKED]: 'Item marked as incomplete',
  [DischargeChecklistHistoryOperation.MARKED_NOT_NEEDED]: 'Item marked as not needed',
  [DischargeChecklistHistoryOperation.MARKED_NEEDED]: 'Item marked as needed',
};

const NOTE_OPERATIONS = [
  DischargeChecklistHistoryOperation.NOTE_ADDED,
  DischargeChecklistHistoryOperation.NOTE_UPDATED,
];

// A note change reads as the note itself, in full and in quotes (Figma); every version is listed.
// Anything else the api adds later has no wording here yet, so it's left out rather than shown as “”.
function describe(entry: DischargeChecklistHistoryEntry) {
  if (NOTE_OPERATIONS.includes(entry.operation)) return `“${entry.noteText ?? ''}”`;

  return STATE_WORDING[entry.operation] ?? null;
}

type Props = {
  reviewId: string;
  item: DischargeChecklistEntry;
  onClose: () => void;
};

// Every change to one item, newest first. Readable by anyone who can see the card, including after
// discharge, and on mobile.
export default function DischargeChecklistHistoryModal({ reviewId, item, onClose }: Props) {
  const query = useDischargeChecklistItemHistory(reviewId, item.itemId);
  // Pages are offsets, so a change made while scrolling can shift an entry onto the next page as well.
  const entries = uniqBy(query.data?.pages.flatMap((page) => page.data) ?? [], 'id').filter(
    (entry) => describe(entry) !== null
  );

  return (
    <StandardModal title='View History' onCancel={onClose}>
      <StandardModal.RecordName>Item: {item.label}</StandardModal.RecordName>
      <HistoryList data-testid='discharge-checklist-history'>
        {query.isLoading && <Status>Loading history…</Status>}
        {query.isError && <Status>History couldn't be loaded. Close this window and try again.</Status>}
        {query.isSuccess && entries.length === 0 && <Status>No changes yet.</Status>}
        {entries.map((entry) => (
          <HistoryEntry key={entry.id} data-testid='discharge-checklist-history-entry'>
            <HistoryContent>{describe(entry)}</HistoryContent>
            <HistoryByline>
              {[entry.createdByName, entry.createdAt && `on ${simpleDateAtTime(entry.createdAt)}`]
                .filter(Boolean)
                .join(' ')}
            </HistoryByline>
          </HistoryEntry>
        ))}
        <MoreContentRequester resource={query} />
      </HistoryList>
      <StandardModal.Actions>
        <Button onClick={onClose}>Close</Button>
      </StandardModal.Actions>
    </StandardModal>
  );
}

const HistoryList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  overflow-y: auto;
  max-height: 400px;
  margin-bottom: 24px;

  @media ${devices.desktop} {
    max-height: 50vh;
  }
`;

const HistoryEntry = styled.div`
  padding-bottom: 16px;
  border-bottom: 1px solid ${colors.black10};
`;

const HistoryContent = styled(BodySmall)`
  margin: 0;
  color: ${colors.black100};
  white-space: pre-wrap;
  word-break: break-word;
`;

const Status = styled(BodySmall)`
  margin: 0;
  color: ${colors.black50};
`;

const HistoryByline = styled(Label)`
  display: block;
  color: ${colors.black50};
  margin-top: 4px;
`;
